import clsx from 'clsx'
import Checkbox from './Checkbox'
import { useCallback, useState } from 'react'

type Props = React.PropsWithChildren<{
  label: string
  description?: string
  serverError?: string
  isRequired?: boolean
  size?: 'regular' | 'small'
  isInitialEnabled?: boolean
  canBeDisabled?: boolean
  onEnabledChange?: (enabled: boolean) => void
}>

export default function Field(props: Props) {
  const {
    children,
    isInitialEnabled = true,
    canBeDisabled = false,
    label,
    serverError,
    description,
    isRequired,
    onEnabledChange,
    size = 'regular'
  } = props

  const [isEnabled, setIsEnabled] = useState(isInitialEnabled)

  const onToggleEnabled = useCallback(() => {
    const newIsEnabled = !isEnabled

    setIsEnabled(newIsEnabled)
    onEnabledChange?.(newIsEnabled)
  }, [setIsEnabled, isEnabled, onEnabledChange])

  return (
    <fieldset
      className={clsx(
        'flex min-w-0 flex-1 flex-col',
        size === 'regular' && 'gap-2.5 p-2.5',
        size === 'small' && 'gap-1 p-0'
      )}
    >
      <div className="flex flex-row items-center gap-2">
        {canBeDisabled && (
          <Checkbox
            checked={isEnabled}
            onChange={onToggleEnabled}
            className={clsx(
              'text-text',
              size === 'regular' && 'text-heading3',
              size === 'small' && 'text-regular font-bold'
            )}
          >
            {label}
            {isRequired ? '*' : ''}
          </Checkbox>
        )}
        {!canBeDisabled && (
          <label
            className={clsx(
              'text-text',
              size === 'regular' && 'text-heading3',
              size === 'small' && 'text-regular font-bold'
            )}
          >
            {label}
            {isRequired ? '*' : ''}
          </label>
        )}
      </div>
      {isEnabled && (
        <>
          <span className="text-regular text-text">{description}</span>
          <div className="flex flex-col gap-1">
            {children}
            {serverError && (
              <span className="pl-2 text-regular text-red-500">
                {serverError}
              </span>
            )}
          </div>
        </>
      )}
    </fieldset>
  )
}
